import { GameConstants } from "../GameConstants";
import { AudioManager } from "../AudioManager";
import { GameVars } from "../GameVars";

export class PreLoader extends Phaser.State {

    public static currentInstance: PreLoader;
    
    private preloadBar: Phaser.Sprite;
    private preloadItems: Phaser.Group;
    private loadingLabel: Phaser.BitmapText;
    private ready: boolean;
    private assetsLoaded: boolean;
    private framesCounter: number;
    
    public init(): void {
        
        PreLoader.currentInstance = this;
        
        this.ready = false;
        this.assetsLoaded = false;
        this.framesCounter = 0;
    }
    
    public preload(): void {

        Phaser.Canvas.setBackgroundColor(this.game.canvas, "#000000");

        this.generateBitmapData();
        this.composeScene();
        this.loadAssets();
    }

    public create(): void {
        
        this.assetsLoaded = true;
        
        if (this.game.device.desktop) {
            this.loadingLabel.visible = false;
        } else {
            this.loadingLabel.text = "lighting the fireplace";
            this.loadingLabel.x = GameConstants.GAME_WIDTH / 2 - this.loadingLabel.width / 2;
        }

        if (!this.game.device.desktop && GameConstants.SPONSOR !== GameConstants.YANDEX) {
            this.game.input.onDown.add(this.goLarge, this);
        }
    }

    public shutdown(): void {
        
        PreLoader.currentInstance = this;
        
        super.shutdown(this.game);
    }

    public goLarge(): void {

        this.game.scale.startFullScreen();
    }
    
    public update(): void {
        
        if (this.cache.isSoundDecoded("audio-sprite") && !this.ready ) {
            
            this.ready = true;
            this.startGame();
        }
        
        if (this.assetsLoaded && !this.game.device.desktop) {
            this.framesCounter++;
            
            if (this.framesCounter % 25 === 0) {
                this.loadingLabel.visible = !this.loadingLabel.visible;
            }
        }
        
        super.update(this.game);
    }
    
    private startGame(): void {
        
        if (GameConstants.SPONSOR === GameConstants.GAMEPIX) {
            
            GamePix.game.gameLoaded(function(): void{
                
                    AudioManager.getInstance().init(PreLoader.currentInstance.game);
                    
                    PreLoader.currentInstance.game.add.tween(PreLoader.currentInstance.preloadItems)
                        .to({alpha: 0 }, 450 , Phaser.Easing.Cubic.Out, true)
                        .onComplete.add(function(): void {   
                            PreLoader.currentInstance.game.state.start("GameState", true, false);
                        }, this);
            });

        } else {
            
            // audio state is stored in a cookie
            AudioManager.getInstance().init(this.game);
        
            // this.preloadItems
            this.game.add.tween(this.preloadItems)
                .to({alpha: 0 }, 450 , Phaser.Easing.Cubic.Out, true)
                .onComplete.add(function(): void {   
                    this.game.state.start("GameState", true, false);
                }, this);
        }
    }
    
    private updateLoadedPercentage(): void {

        this.preloadBar.scale.x = this.load.progress / 100 * 22.5 * GameVars.scaleX;
        
        if (GameConstants.SPONSOR === GameConstants.GAMEPIX) {
            GamePix.game.gameLoading(this.load.progress);
        }
    }

    private generateBitmapData(): void {

        let bmd: Phaser.BitmapData = this.game.add.bitmapData( 16 , 16, "black-square", true);
        bmd.ctx.beginPath();
        bmd.ctx.rect(0, 0, 16, 16);
        bmd.ctx.fillStyle = "#000000";
        bmd.ctx.fill();
        
        bmd = this.game.add.bitmapData( 16 , 16, "white-square", true);
        bmd.ctx.beginPath();
        bmd.ctx.rect(0, 0, 16, 16);
        bmd.ctx.fillStyle = "#ffffff";
        bmd.ctx.fill();
        
        bmd = this.game.add.bitmapData( 16 , 16, "lila-square", true);
        bmd.ctx.beginPath();
        bmd.ctx.rect(0, 0, 16, 16);
        bmd.ctx.fillStyle = "#3d5fa2";
        bmd.ctx.fill();
                    
        bmd = this.game.add.bitmapData( 16 , 16, "yellow-square", true);
        bmd.ctx.beginPath();
        bmd.ctx.rect(0, 0, 16, 16);
        bmd.ctx.fillStyle = "#FFE781";
        bmd.ctx.fill();
        
        bmd = this.game.add.bitmapData( 16 , 16, "blue-square", true);
        bmd.ctx.beginPath();
        bmd.ctx.rect(0, 0, 16, 16);
        bmd.ctx.fillStyle = "#0000ff";
        bmd.ctx.fill();
        
        bmd = this.game.add.bitmapData( 16 , 16, "preload-bar-capsule", true);
        bmd.ctx.beginPath();
        bmd.ctx.rect(0, 0, 16, 16);
        bmd.ctx.fillStyle = "#609ccf";
        bmd.ctx.fill();
        
        bmd = this.game.add.bitmapData( 16 , 16, "preload-bar-background", true);
        bmd.ctx.beginPath();
        bmd.ctx.rect(0, 0, 16, 16);
        bmd.ctx.fillStyle = "#1d2937";
        bmd.ctx.fill();
        
        bmd = this.game.add.bitmapData( 16 , 16, "preload-bar", true);
        bmd.ctx.beginPath();
        bmd.ctx.rect(0, 0, 16, 16);
        bmd.ctx.fillStyle = "#fea66b";
        bmd.ctx.fill();
    }

    private composeScene(): void {
        
        const blurredStage = this.add.image(0, 0, "blurred-stage");
        blurredStage.width = GameConstants.GAME_WIDTH;
        blurredStage.height = GameConstants.GAME_HEIGHT;
        
        this.preloadItems = this.add.group();
        
        const gameLogo: Phaser.Image = this.add.image( GameConstants.GAME_WIDTH / 2, 230, "game-logo", null);
        gameLogo.anchor.x = .5;
        gameLogo.scale.x = GameVars.scaleX;
        this.preloadItems.add(gameLogo);
        
        this.loadingLabel = this.add.bitmapText( GameConstants.GAME_WIDTH / 2, 420, "marker-felt", "we are placing some logs in the fireplace...", 23);
        this.loadingLabel.scale.x = GameVars.scaleX; 
        this.loadingLabel.x -= this.loadingLabel.width / 2;
        this.preloadItems.add(this.loadingLabel);
        
        const preloadBarCapsuleShadow: Phaser.Sprite = new Phaser.Sprite(this.game, GameConstants.GAME_WIDTH / 2 + 2, 400 + 2, this.game.cache.getBitmapData("black-square"));
        preloadBarCapsuleShadow.scale.setTo(22.65 * GameVars.scaleX, .85);
        preloadBarCapsuleShadow.anchor.setTo(.5, .5);
        preloadBarCapsuleShadow.alpha = .45;
        this.preloadItems.add(preloadBarCapsuleShadow);
                
        const preloadBarCapsule: Phaser.Sprite = new Phaser.Sprite(this.game, GameConstants.GAME_WIDTH / 2, 400, this.game.cache.getBitmapData("preload-bar-capsule"));
        preloadBarCapsule.scale.setTo(22.65 * GameVars.scaleX, .85);
        preloadBarCapsule.anchor.setTo(.5, .5);
        this.preloadItems.add(preloadBarCapsule);
        
        const preloadBackground: Phaser.Sprite = new Phaser.Sprite(this.game,  GameConstants.GAME_WIDTH / 2 - 180 * GameVars.scaleX, 400, this.game.cache.getBitmapData("preload-bar-background"));
        preloadBackground.scale.setTo(22.5 * GameVars.scaleX, .6);
        preloadBackground.anchor.setTo(0, .5);
        this.preloadItems.add(preloadBackground);

        this.preloadBar = new Phaser.Sprite(this.game, GameConstants.GAME_WIDTH / 2 - 180 * GameVars.scaleX, 400, this.game.cache.getBitmapData("preload-bar"));
        this.preloadBar.scale.setTo(0, .6);
        this.preloadBar.anchor.setTo(0, .5);
        this.preloadItems.add(this.preloadBar);
        
        if (GameConstants.SPONSOR !== GameConstants.FUNO) {
            const copyRightLabel = this.add.text(5, GameConstants.GAME_HEIGHT  - 22, "made by RavalMatic, licensed to: " + GameConstants.SPONSOR,  { font: "16px  Arial", fill: "#ffffff"});
            copyRightLabel.scale.x = GameVars.scaleX;
            copyRightLabel.alpha = .35;
        }

        if (GameConstants.SPONSOR === GameConstants.SOLITAIRE_KOSTENLOS) {
            const solitaireKostenlosLogo = new Phaser.Image(this.game,  GameConstants.GAME_WIDTH / 2, 355, "solitaire-kostenlos-logo");
            solitaireKostenlosLogo.anchor.set(.5);
            solitaireKostenlosLogo.scale.x = GameVars.scaleX * .75;
            solitaireKostenlosLogo.scale.y = .75;
            this.preloadItems.add(solitaireKostenlosLogo);
        }
    }
    
    private loadAssets(): void {

        // audio
        this.load.audiosprite("audio-sprite", ["assets/audio/audio-sprite.mp3", "assets/audio/audio-sprite.ogg"], "assets/audio/audio-sprite.json", true);
        
        // atlas containing cheat buttons only loaded while developing
        if (GameConstants.DEVELOPMENT) {
            this.load.atlas("texture_atlas_0", "assets/texture_atlas_0.png", "assets/texture_atlas_0.json");
        }

        this.load.atlas("texture_atlas_1", "assets/texture_atlas_1.png", "assets/texture_atlas_1.json");
        this.load.atlas("texture_atlas_2", "assets/texture_atlas_2.png", "assets/texture_atlas_2.json");
        
        // fonts
        this.load.bitmapFont("beacon", "assets/fonts/beacon.png", "assets/fonts/beacon.xml");

        // deal tween card positions
        this.load.json("card-positions", "assets/card-positions.json");
        
        if (GameConstants.SPONSOR === GameConstants.MINIJUEGOS) {
            this.load.image("sponsor-logo", "assets/logo-minijuegos.png");  
        }
        
        this.load.onFileComplete.add( this.updateLoadedPercentage, this);
    }
}
